import type { EventData, HeightCorrection, Poi, Polygon, Restriction } from "../../api/client";
import type { FC, LatLonPt } from "../../map/RouteMap";

export const fc = (features: GeoJSON.Feature[]): FC => ({ type: "FeatureCollection", features });

export function restrictionsFc(rs: Restriction[], selectedId: string | null): FC {
  return fc(rs.map((r) => ({
    type: "Feature",
    properties: { id: r.id, kind: r.kind, enabled: r.enabled !== false, crossable: r.crossable ?? null, selected: r.id === selectedId, label: r.label ?? "" },
    geometry: r.geometry as GeoJSON.Geometry,
  })));
}

/** ids of the restrictions of an overlay feature: an array, or its JSON text once read back from the map */
export function featureRestrictionIds(p: Record<string, unknown> | null | undefined): string[] {
  const v = p?.restrictionIds;
  if (Array.isArray(v)) return v.map(String);
  if (typeof v === "string") { try { const a = JSON.parse(v); return Array.isArray(a) ? a.map(String) : []; } catch { return []; } }
  return [];
}

/** stretches of a course snapped on the roads, from the network effects (COURSE, COURSE_UPCOMING) */
export function snappedCourse(overlay: FC, id: string): GeoJSON.Geometry[] {
  return overlay.features
    .filter((f) => (f.properties?.effect === "COURSE" || f.properties?.effect === "COURSE_UPCOMING") && featureRestrictionIds(f.properties).includes(id))
    .map((f) => f.geometry);
}

/**
 * What the operator entered, as drawn by the editor (1.1.1): a course is only shown snapped on the roads (network
 * effects), never as drawn. The selected course is highlighted on its snapped stretches (from the effects, else
 * from the snapping preview of the form); its drawn line only shows when nothing is snapped (disabled, ended or
 * not snapped) and while it is redrawn ([draftLine], thin).
 */
export function editorRestrictionsFc(rs: Restriction[], selectedId: string | null, overlay: FC, snapLine: GeoJSON.Geometry | null,
                                     draft?: { kind: Restriction["kind"]; geometry: Restriction["geometry"]; existingId?: string }): FC {
  const features: GeoJSON.Feature[] = [];
  for (const f of restrictionsFc(rs, selectedId).features) {
    const p = f.properties!;
    if (draft && p.id === draft.existingId) continue;
    if (p.kind !== "COURSE") { features.push(f); continue; }
    if (!p.selected) continue;
    const snapped = snappedCourse(overlay, String(p.id));
    const lines = snapped.length > 0 ? snapped : snapLine ? [snapLine] : [];
    if (lines.length === 0) features.push(f);
    else for (const g of lines) features.push({ type: "Feature", properties: { ...p, snapped: true }, geometry: g });
  }
  if (draft) {
    const course = draft.kind === "COURSE";
    const props = { id: "__draft", kind: draft.kind, enabled: true, crossable: null, selected: true };
    features.push({ type: "Feature", properties: { ...props, original: course }, geometry: draft.geometry as GeoJSON.Geometry });
    if (course && snapLine) features.push({ type: "Feature", properties: { ...props, snapped: true }, geometry: snapLine });
  }
  return fc(features);
}

export function poisFc(pois: Poi[], categories: EventData["poiCategories"], selectedId: string | null): FC {
  const colors = new globalThis.Map((categories ?? []).map((c) => [c.id, c.color ?? "#2f9e44"]));
  return fc(pois.map((p) => ({
    type: "Feature",
    properties: { id: p.id, name: p.name, categoryId: p.categoryId, color: colors.get(p.categoryId) ?? "#2f9e44", selected: p.id === selectedId },
    geometry: p.location as GeoJSON.Geometry,
  })));
}

export function hcFc(hcs: HeightCorrection[], selectedId: string | null): FC {
  return fc(hcs.map((h) => ({ type: "Feature", properties: { id: h.id, maxHeightM: h.maxHeightM, comment: h.comment ?? "", selected: h.id === selectedId }, geometry: h.geometry as GeoJSON.Geometry })));
}

export function polygonFc(p: Polygon | undefined | null): FC {
  return p ? fc([{ type: "Feature", properties: {}, geometry: p as GeoJSON.Geometry }]) : fc([]);
}

export function geometryPoints(g: { type: string; coordinates: unknown }): [number, number][] {
  if (g.type === "Point") return [g.coordinates as [number, number]];
  if (g.type === "LineString") return g.coordinates as [number, number][];
  if (g.type === "Polygon") return (g.coordinates as [number, number][][])[0] ?? [];
  return [];
}

export function eventPoints(e: EventData): [number, number][] {
  const pts: [number, number][] = [];
  for (const r of e.restrictions ?? []) pts.push(...geometryPoints(r.geometry));
  for (const p of e.pois ?? []) pts.push(p.location.coordinates as [number, number]);
  if (e.zone) pts.push(...geometryPoints(e.zone));
  return pts;
}

export const toPolygon = (pts: LatLonPt[]): Polygon => ({ type: "Polygon", coordinates: [[...pts, pts[0]].map((p) => [p.lon, p.lat])] });
export const toPoint = (p: LatLonPt) => ({ type: "Point" as const, coordinates: [p.lon, p.lat] });
export const toLine = (pts: LatLonPt[]) => ({ type: "LineString" as const, coordinates: pts.map((p) => [p.lon, p.lat]) });

/** ray casting on the outer ring (same rule as core-model Polygon.contains) */
export function polygonContains(poly: Polygon, lon: number, lat: number): boolean {
  const ring = poly.coordinates[0] ?? [];
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if (yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

export function outsideZone(e: EventData): number {
  if (!e.zone) return 0;
  let n = 0;
  for (const r of e.restrictions ?? []) if (!geometryPoints(r.geometry).every(([lon, lat]) => polygonContains(e.zone!, lon, lat))) n++;
  for (const p of e.pois ?? []) { const [lon, lat] = p.location.coordinates; if (!polygonContains(e.zone, lon, lat)) n++; }
  return n;
}

export const isActiveNow = (e: EventData, now: Date) => e.status === "PUBLISHED" && now.getTime() >= Date.parse(e.startAt) && now.getTime() < Date.parse(e.endAt);
